import { TurnOffDataAccelerationForViews, TurnOnDataAccelerationForViews } from '~src/dataAcceleration/serverApi/requests/DataAccelerationRequest';

import { WebClientDetailFetcher } from '../../commonInfra/utils/resourceFetchers/WebClientDetailFetcher';
import { WebClientListResourceFetcher } from '../../commonInfra/utils/resourceFetchers/WebClientListResourceFetcher';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { RequestHelper } from '../../httpClient/utils/RequestHelper';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { IDetailFetcher } from '../../ts/fetchers/IDetailFetcher';
import { IListFetcher } from '../../ts/fetchers/IListFetcher';
import { ServerFilterClauses } from '../../ts/helpers/ServerFilterClauses';
import { ServerApiMapper } from '../../ts/helpers/ServerModelHelper';
import { ViewMapper } from '../../ts/mappers/ViewMapper';
import { ITaggableContent } from '../../ts/models/ITaggableContent';
import { IViewBase, IViewDetailInfo, IViewInfo } from '../../ts/models/IViewInfo';
import { FieldValuesQuery } from '../../ts/resources/FieldValuesQuery';
import { IActionResource, IDetailResource, IFieldValuesResource, IListResource } from '../../ts/resources/Resource';
import { ResourceQuery } from '../../ts/resources/ResourceQuery';
import { AddTagsToViewsRequest } from '../../ts/ServerApi/requests/AddTagsToViewsRequest';
import { GetFieldValuesRequest } from '../../ts/ServerApi/requests/GetFieldValuesRequest';
import { GetViewActionsRequest } from '../../ts/ServerApi/requests/GetViewActionsRequest';
import { GetViewByPathRequest, IGetViewByPathResult } from '../../ts/ServerApi/requests/GetViewByPathRequest';
import { GetViewsRequest } from '../../ts/ServerApi/requests/GetViewsRequest';
import { HideViewsRequest } from '../../ts/ServerApi/requests/HideViewsRequest';
import { MarkRecentlyViewedRequest, RecentlyViewedObjectType } from '../../ts/ServerApi/requests/MarkRecentlyViewedRequest';
import { RemoveTagsFromViewsRequest } from '../../ts/ServerApi/requests/RemoveTagsFromViewsRequest';
import { RenameViewRequest } from '../../ts/ServerApi/requests/RenameViewRequest';
import { IFieldValue } from '../../ts/ServerApi/types/IFieldValue';
import { IViewActions } from '../../ts/ServerApi/types/IViewActions';
import { IWorkbookBase, IWorkbookParentInfo } from '../../workbook/models/IWorkbookInfo';

class ViewsResource implements IListResource<IViewInfo>, IFieldValuesResource, IDetailResource<IViewDetailInfo>, IActionResource<IViewActions>, ITaggableContent {

  private listFetcher: IListFetcher<IViewInfo> = new WebClientListResourceFetcher(GetViewsRequest, ServerApiMapper.views);
  private detailFetcher: IDetailFetcher<IViewDetailInfo> = new WebClientDetailFetcher('getView', ViewMapper.mapDetail)
  private fieldValuesFetcher: IListFetcher<IFieldValue> = new WebClientListResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);

  public isViewDetailInfo(item: any): item is IViewDetailInfo {
    return item.contentType &&
      item.contentType === 'view' &&
      (<IViewDetailInfo>item).hasOwnProperty('sheetType');
  }

  public isViewBase(item: any): item is IViewBase {
    return item.contentType &&
      item.contentType === 'view' &&
      item.hasOwnProperty('workbook');
  }

  public all(): ResourceQuery<IViewInfo> {
    return new ResourceQuery(this.listFetcher);
  }

  public getById(id: string): Promise<IViewDetailInfo> {
    return this.detailFetcher.fetchById(id);
  }

  public getByPath(path: string): Promise<IViewDetailInfo> {
    // TFS 1313385: location is partially encoded by the httpd apache backend,
    // which messes up fetching the embedded viz url if we don't remove the encodings.
    // Using decodeURI vs decodeURIComponent to preserve intential ampersand encodings better
    const decodedPath = decodeURI(path);

    return WebHttpClient.sendRequest(new GetViewByPathRequest(decodedPath))
      .then((result: IGetViewByPathResult) => {
        const error = result?.errors?.[0].code;
        if (error) {
          return Promise.reject(error);
        } else {
          return Promise.resolve(ViewMapper.mapDetail(result));
        }
      });
  }

  public forWorkbook(workbook: IWorkbookBase | IWorkbookParentInfo): ResourceQuery<IViewInfo> {
    return this.all().withFilters(ServerFilterClauses.create({ workbook: workbook.id }));
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'view');
  }

  // Actions

  public actions(id: string): Promise<IViewActions> {
    return WebHttpClient.sendRequest(new GetViewActionsRequest(id));
  }

  public rename(id: string, name: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new RenameViewRequest(id, name));
  }

  public hide(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new HideViewsRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public addTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new AddTagsToViewsRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public removeTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new RemoveTagsFromViewsRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public changeTags(ids: string[], tagsToAdd: string[], tagsToRemove: string[]): Promise<IResult> {
    return this.removeTags(ids, tagsToRemove)
      .then((result: IResult) => (ResultHelper.hasErrors(result) ? result : this.addTags(ids, tagsToAdd)));
  }

  public markRecentlyViewed(id: string) {
    return WebHttpClient.sendRequest(new MarkRecentlyViewedRequest({
      objectId: id,
      objectType: RecentlyViewedObjectType.View
    }));
  }

  public turnOnDataAccelerationForViews(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids,
        (idsBlock) => new TurnOnDataAccelerationForViews({
          targetIds: idsBlock,
        })),
      ResultHelper.combineResults);
  }

  public turnOffDataAccelerationForViews(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids,
        (idsBlock) => new TurnOffDataAccelerationForViews({
          targetIds: idsBlock,
        })),
      ResultHelper.combineResults);
  }
}

export const Views = new ViewsResource();
